import { MenuDrawer } from '@/scripts/theme/menu-drawer'
import { getFocusableElements, qsRequired } from '@/scripts/core/global'
import { trapFocus } from '@/scripts/core/global'
import { ATTRIBUTES, SELECTORS } from '@/scripts/core/global'

export class HeaderDrawer extends MenuDrawer {
	static override htmlSelector = 'header-drawer'
	static override selectors = {
		...MenuDrawer.selectors,
	}
	header?: HTMLElement
	borderOffset?: number
	constructor() {
		super()
		this.addEventListener('keydown', this.onHeaderKeyDown.bind(this))
	}

	onHeaderKeyDown(event: KeyboardEvent) {
		if (event.code !== 'Tab' || !this.mainDetails.open) return
		// The shared trap includes links inside closed details; only visible header links can wrap.
		const elements = getFocusableElements(this.mainDetails).filter(
			(el) => el.getClientRects().length
		)
		const first = elements[0]
		const last = elements[elements.length - 1]
		if (
			first &&
			last &&
			((event.shiftKey && event.target === first) ||
				(!event.shiftKey && event.target === last))
		) {
			event.preventDefault()
			event.stopPropagation()
			;(event.shiftKey ? last : first).focus()
		}
	}

	override bindEvents() {
		this.mainSummary.removeEventListener('click', this.onHeaderSummaryClick)
		this.mainSummary.addEventListener('click', this.onHeaderSummaryClick)
		this.querySelectorAll('details').forEach((details) => {
			details.removeEventListener('toggle', this.onDetailsToggle)
			details.addEventListener('toggle', this.onDetailsToggle)
			qsRequired('summary', details).setAttribute('aria-expanded', `${details.open}`)
		})
	}

	onHeaderSummaryClick(event: MouseEvent) {
		event.preventDefault()
		const summary = event.currentTarget as HTMLElement
		const drawer = summary.closest<HeaderDrawer>('header-drawer')
		drawer?.onMainSummaryClick(event)
	}

	onDetailsToggle(event: Event) {
		const details = event.currentTarget as HTMLDetailsElement
		qsRequired('summary', details).setAttribute('aria-expanded', `${details.open}`)
		const drawer = details.closest<HeaderDrawer>('header-drawer')
		// Inline groups share the root focus trap; their visible endpoints change on toggle.
		if (drawer?.mainDetails.open && drawer.mainDetails.hasAttribute(ATTRIBUTES.menuOpening)) {
			const active = document.activeElement
			trapFocus(
				drawer.mainDetails,
				active instanceof HTMLElement && drawer.contains(active)
					? active
					: drawer.mainSummary
			)
		}
	}

	override closeSubmenu(detailsElement: HTMLElement) {
		detailsElement.removeAttribute('open')
		const summary = qsRequired('summary', detailsElement)
		summary.setAttribute('aria-expanded', 'false')
		trapFocus(this.mainDetails, summary)
	}

	override getInstanceSelectors() {
		this.instanceSelectors = HeaderDrawer.selectors
	}

	setHeader() {
		return this.header || qsRequired(SELECTORS.sectionHeader)
	}

	setBorderOffset() {
		return this.borderOffset ||
			qsRequired(SELECTORS.headerWrapper).dataset.ucHeaderWrapper == 'border-bottom'
			? 1
			: 0
	}

	override openMenuDrawer(summaryElement: HTMLElement) {
		this.mainDetails.open = true
		this.header = this.setHeader()
		this.borderOffset = this.setBorderOffset()
		document.documentElement.style.setProperty(
			'--header-bottom-position',
			`${Math.round(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
		)
		this.header.setAttribute('data-uc-header-menu-open', '')

		setTimeout(() => {
			if (!this.mainDetails.open) return
			this.mainDetails.setAttribute(ATTRIBUTES.menuOpening, '')
			trapFocus(this.mainDetails, summaryElement)
		})

		summaryElement.setAttribute('aria-expanded', 'true')
		window.addEventListener('resize', this.onResize)
		trapFocus(this.mainDetails, summaryElement)
		document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`)
	}

	override closeMenuDrawer(
		event: Event | undefined,
		elementToFocus: HTMLElement | undefined = undefined
	) {
		if (!event) return
		this.header = this.setHeader()
		super.closeMenuDrawer(event, elementToFocus)
		this.mainSummary.setAttribute('aria-expanded', 'false')
		this.subSummaries?.forEach((summary) => summary.setAttribute('aria-expanded', 'false'))
		this.header.removeAttribute('data-uc-header-menu-open')
		window.removeEventListener('resize', this.onResize)
	}

	override closeAnimation(detailsElement: HTMLElement) {
		// Native inline details close immediately, so a stale animation cannot close a reopened menu.
		detailsElement.removeAttribute('open')
	}

	onResize = () => {
		if (!this.header) return
		this.borderOffset = this.setBorderOffset()
		document.documentElement.style.setProperty(
			'--header-bottom-position',
			`${Math.round(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
		)
		document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`)
	}
}
